import React, { useEffect, useId, useRef, useState } from "react";
import { strings } from "../../resources/strings";
import { isNonNumericName } from "../../utils/inputValidation";
import { withRequiredSuffix } from "./workflow/workflowLabels";

/** Max length for each typed name field (the signer attestation). */
const MAX_NAME_LENGTH = 100;
/** Delay before closing the wizard after a successful submit. */
const SUCCESS_CLOSE_DELAY_MS = 1500;

type SubmitStatus = "idle" | "submitting" | "success" | "error";

export interface FinalStatementScreenProps {
  /**
   * Submits the assembled answers. Returns true on success, false on failure.
   * Must never reject — the caller catches and resolves false — and must not
   * unmount this screen, so a failed submit keeps the gathered answers and the
   * typed name for retry.
   */
  onSubmit: (signerName: string) => Promise<boolean>;
  /**
   * Called once after a SUCCESSFUL submit (completion close) — distinct from the
   * toolbar's dismiss/abandon. The host uses this to mark the check-in complete.
   */
  onComplete: () => void;
}

/**
 * Final Statement screen (Figma "Final Statement 2.1"). Shown once every
 * question is answered: the participant types their name as a signed
 * attestation and submits the report. Owns the submit/loading/success/error
 * state so the flow engine stays mounted and answers survive a failed retry.
 */
const FinalStatementScreen: React.FC<FinalStatementScreenProps> = ({
  onSubmit,
  onComplete,
}) => {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const firstNameId = useId();
  const lastNameId = useId();

  // Guard post-await state updates and clear the success timer if the wizard is
  // closed/unmounted mid-flight, so onComplete's side effects can't double-fire.
  const mountedRef = useRef(true);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => {
    // Set true on every mount — NOT just via the initial ref value — so that
    // StrictMode's mount→cleanup→mount cycle (which runs the cleanup below)
    // leaves mountedRef true, instead of stranding a post-await update.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const signerName = `${firstName.trim()} ${lastName.trim()}`.trim();

  // A signature name can't be purely numeric/punctuation (e.g. typos landing
  // on the number row) — only flagged once the field has content, so the
  // "required" state (empty) isn't also shown as a format error.
  const firstNameError =
    firstName.trim().length > 0 && !isNonNumericName(firstName)
      ? strings.reportForm.finalStatement.nameInvalid
      : undefined;
  const lastNameError =
    lastName.trim().length > 0 && !isNonNumericName(lastName)
      ? strings.reportForm.finalStatement.nameInvalid
      : undefined;

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    !firstNameError &&
    !lastNameError &&
    status !== "submitting";

  // Submit is gated solely by the button's `disabled` below (names present and
  // not already in flight) — so this handler needs no duplicate guard, which
  // would be unreachable behind `disabled` (matches TextQuestion).
  const handleSubmit = async () => {
    setStatus("submitting");
    // onSubmit is contracted not to reject (the host catches and resolves false),
    // but guard defensively so a rejection can never leave the UI stuck in
    // "submitting" with no retry path.
    let ok = false;
    try {
      ok = await onSubmit(signerName);
    } catch {
      ok = false;
    }
    if (!mountedRef.current) {
      return;
    }
    if (ok) {
      setStatus("success");
      closeTimerRef.current = setTimeout(onComplete, SUCCESS_CLOSE_DELAY_MS);
    } else {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      // role="status" (polite): the success block replaces the form without
      // focus moving to it, so nothing would otherwise announce the outcome
      // (SC 4.1.3). Polite rather than "alert" because success is not an error
      // and should not interrupt. NOTE: onComplete closes the wizard after
      // SUCCESS_CLOSE_DELAY_MS, which may cut the announcement short — logged
      // in A11Y_ISSUES.md; lengthening it is a UX change outside this batch.
      <div
        data-testid="final-statement-success"
        role="status"
        className="mt-6 flex flex-col items-center gap-3 text-center"
      >
        <h2 className="text-2xl text-black">
          {strings.reportForm.finalStatement.successHeading}
        </h2>
        <p className="text-base text-primary">
          {strings.reportForm.finalStatement.successBody}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="final-statement"
      className="mt-6 flex flex-col gap-6 overflow-y-auto"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm text-[#515964]">
          {strings.reportForm.finalStatement.topic}
        </p>
        <h2 className="text-2xl text-black">
          {strings.reportForm.finalStatement.heading}
        </h2>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor={firstNameId} className="text-sm text-[#515964]">
          {withRequiredSuffix(
            strings.reportForm.finalStatement.firstNameLabel,
            true,
          )}
        </label>
        <input
          id={firstNameId}
          data-testid="final-statement-first-name"
          type="text"
          value={firstName}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setFirstName(e.target.value)}
          required
          aria-required
          autoComplete="given-name"
          aria-invalid={Boolean(firstNameError)}
          aria-describedby={firstNameError ? `${firstNameId}-error` : undefined}
          className={`w-full min-h-[44px] p-2 border rounded-lg ${
            firstNameError ? "border-red-600" : "border-primary"
          }`}
        />
        {firstNameError && (
          <p
            id={`${firstNameId}-error`}
            data-testid="final-statement-first-name-error"
            className="text-xs text-red-600"
          >
            {firstNameError}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor={lastNameId} className="text-sm text-[#515964]">
          {withRequiredSuffix(
            strings.reportForm.finalStatement.lastNameLabel,
            true,
          )}
        </label>
        <input
          id={lastNameId}
          data-testid="final-statement-last-name"
          type="text"
          value={lastName}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setLastName(e.target.value)}
          required
          aria-required
          autoComplete="family-name"
          aria-invalid={Boolean(lastNameError)}
          aria-describedby={lastNameError ? `${lastNameId}-error` : undefined}
          className={`w-full min-h-[44px] p-2 border rounded-lg ${
            lastNameError ? "border-red-600" : "border-primary"
          }`}
        />
        {lastNameError && (
          <p
            id={`${lastNameId}-error`}
            data-testid="final-statement-last-name-error"
            className="text-xs text-red-600"
          >
            {lastNameError}
          </p>
        )}
      </div>

      {status === "error" && (
        // role="alert" (assertive): a failed submit renders this block in
        // place while focus is still on the Submit button, so it is a genuine
        // "appears while focus is elsewhere" status message (SC 4.1.3).
        // Field-level errors deliberately do NOT get a role — they are derived
        // from the value on each render and appear as the participant types,
        // with focus already in the field, where aria-describedby serves; an
        // alert there would re-announce on every keystroke.
        <div
          data-testid="final-statement-error"
          role="alert"
          className="flex flex-col gap-1"
        >
          <p className="text-sm font-semibold text-red-600">
            {strings.reportForm.finalStatement.errorHeading}
          </p>
          <p className="text-sm text-red-600">
            {strings.reportForm.finalStatement.errorBody}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full min-h-[44px] h-16 rounded-2xl bg-primary text-accent font-semibold disabled:opacity-30"
      >
        {status === "submitting" ? (
          <span className="flex items-center justify-center gap-2">
            <span
              data-testid="final-statement-spinner"
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent"
            />
            {strings.reportForm.finalStatement.submitting}
          </span>
        ) : status === "error" ? (
          strings.reportForm.finalStatement.retry
        ) : (
          strings.reportForm.finalStatement.submit
        )}
      </button>
    </div>
  );
};

export default FinalStatementScreen;
